// src/components/ecommerce/ServiceGrid.jsx
import React, { useState } from 'react';
import { 
  Clock,
  Users,
  MessageCircle,
  Calendar,
  Star,
  Info,
  CheckCircle,
  MapPin,
  DollarSign,
  Zap,
  Award,
  TrendingUp
} from 'lucide-react';
import { ServiceCard } from './ServiceCard';

// src/components/ecommerce/ServiceGrid.jsx - FIX BOOKING DATA FLOW

export const ServiceGrid = ({ 
  services, 
  storeView = 'grid', 
  onServiceClick, 
  onBookService,
  onContactAboutService,
  onViewBooking,
  bookedServiceIds = [],
  styling = {},
  storeSettings = {},
  className = '' 
}) => {
  const [loadingServices, setLoadingServices] = useState({});
  const [selectedService, setSelectedService] = useState(null);
const [selectedServiceOptions, setSelectedServiceOptions] = useState({});

  const handleBookClick = (service) => {
    console.log('📘 ServiceGrid: Book clicked for:', service.name);
    console.log('Service has options:', service.options?.length || 0);
    
    if (service.options && service.options.length > 0) {
      // Service has options - show modal
      setSelectedService(service);
      setSelectedServiceOptions({});
    } else {
      // No options - book directly
      handleDirectBooking(service, []);
    }
  };

  const handleDirectBooking = async (service, selectedOptions = []) => {
    setLoadingServices(prev => ({ ...prev, [service.id]: true }));
    
    try {
      // Create bookingData object with proper structure
      const bookingData = {
        service: {
          id: service.id,
          name: service.name,
          description: service.description,
          price: service.price,
          duration: service.duration,
          deliveryMethod: service.deliveryMethod,
          image: service.images?.[0] || null
        },
        selectedOptions,
        totalPrice: calculateTotal(service, selectedOptions)
      };
      
      console.log('✅ ServiceGrid: Booking service with data:', bookingData);
      await onBookService?.(bookingData);
    } catch (error) {
      console.error('Error booking service:', error);
    } finally {
      setLoadingServices(prev => ({ ...prev, [service.id]: false }));
    }
  };

  // ✅ NEW: Calculate total including options
  const calculateTotal = (service, selectedOptions = []) => {
    let total = parseFloat(service.price) || 0;
    
    selectedOptions.forEach(option => {
      if (option.quantity > 0 && !option.isFree) {
        total += option.price * option.quantity;
      }
    });
    
    return parseFloat(total.toFixed(2));
  };

  // ✅ NEW: Handle option changes in modal
  const handleOptionChange = (optionIndex, quantity) => {
    setSelectedServiceOptions(prev => ({
      ...prev,
      [optionIndex]: quantity
    }));
  };

  // ✅ NEW: Confirm booking from options modal
  const handleConfirmBooking = () => {
    console.log('🎯 ServiceGrid: Confirming booking with options:', selectedServiceOptions);
    
    // Convert selectedOptions object to array with proper structure
    const optionsArray = Object.entries(selectedServiceOptions)
      .filter(([_, quantity]) => quantity > 0)
      .map(([index, quantity]) => {
        const option = selectedService.options[parseInt(index)];
        return {
          name: option.name,
          description: option.description,
          price: option.price,
          quantity,
          isFree: option.isFree,
          groupIndex: option.groupIndex ?? null,
          optionIndex: parseInt(index)
        };
      });
    
    console.log('📦 ServiceGrid: Options array:', optionsArray);
    
    handleDirectBooking(selectedService, optionsArray);
    setSelectedService(null);
    setSelectedServiceOptions({});
  };

  // ✅ FIX: Accept bookingData object, not just service
  const handleBookService = async (bookingData) => {
    const serviceId = bookingData?.service?.id;
    
    if (!serviceId) {
      console.error('Invalid booking data:', bookingData);
      return;
    }

    setLoadingServices(prev => ({ ...prev, [serviceId]: true }));
    
    try {
      await onBookService?.(bookingData); // ✅ Pass the full bookingData object
    } catch (error) {
      console.error('Error booking service:', error);
    } finally {
      setLoadingServices(prev => ({ ...prev, [serviceId]: false }));
    }
  };

  const handleContact = async (service) => {
    onContactAboutService?.(service);
  };

  const getBookingCount = (serviceId) => {
    const bookings = bookedServiceIds.filter(id => id === serviceId);
    return bookings.length;
  };

  const isServiceBooked = (serviceId) => {
    return bookedServiceIds.includes(serviceId);
  };

  const getGridClasses = () => {
    switch (storeView) {
      case 'list':
        return 'space-y-6';
      case 'masonry':
        return 'columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6';
      case 'grid':
      default:
        return 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';
    }
  };

  if (!services || services.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-lg border-2 border-dashed border-gray-300">
        <Users className="mx-auto h-16 w-16 text-gray-400 mb-4" />
        <h3 className="text-xl font-semibold text-gray-900 mb-2">No Services Available</h3>
        <p className="text-gray-600 mb-6">
          Check back later for available services or contact us for custom requests.
        </p>
        <button
          onClick={() => onContactAboutService?.(null)}
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <MessageCircle className="h-4 w-4" />
          Contact Us
        </button>
      </div>
    );
  }

  return (
    <div className={`${getGridClasses()} ${className}`}>
      {services.map((service) => (
        <ServiceCard
          key={service.id}
          service={service}
          storeView={storeView}
          onClick={() => onServiceClick?.(service)}
          onBook={() => handleBookClick(service)}
          onContact={() => handleContact(service)}
          onViewBooking={onViewBooking}
          isBooked={isServiceBooked(service.id)}
          bookingCount={getBookingCount(service.id)}
          loading={loadingServices[service.id]}
          styling={styling}
          storeSettings={storeSettings}
        />
      ))}
    </div>
  );
};